import { createFileRoute } from "@tanstack/react-router";
import { Controller } from "react-hook-form";
import type { z } from "zod";

import { CheckboxGroupField } from "~/components/form/checkbox-group-field";
import { ConditionalField } from "~/components/ui/conditional-field";
import { DiscoveryStep } from "~/components/ui/discovery-step";
import { FormField } from "~/components/ui/form-field";
import { Textarea } from "~/components/ui/textarea";
import { useDiscoveryForm } from "~/lib/hooks/use-discovery-form";
import {
  type IdentifySpecificGoalsBaseSchema,
  IdentifySpecificGoalsSchema,
  goalsField,
  otherGoalField,
} from "~/lib/schemas/coach-discovery";
import { createBrandMeta } from "~/lib/shared/meta";

type IdentifySpecificGoalsData = z.infer<
  typeof IdentifySpecificGoalsBaseSchema
>;

const IdentifySpecificGoalsForm = () => {
  const { form, handleNextStep } = useDiscoveryForm({
    step: 1,
    schema: IdentifySpecificGoalsSchema,
  });
  const { handleSubmit, control, watch, setFocus, formState } = form;
  const { errors, isSubmitting, isValid } = formState;

  const watchedGoals = watch("goals") || [];
  const tooManyGoals = watchedGoals.length > 2;

  const onSubmit = (_data: IdentifySpecificGoalsData) => {
    void handleNextStep();
  };

  return (
    <form
      id="quiz-start"
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col"
    >
      <DiscoveryStep.Card className="mt-0! space-y-6">
        <FormField error={errors.goals?.message}>
          <CheckboxGroupField
            name="goals"
            control={control}
            options={goalsField.options || []}
            onTrigger={{
              Other: () => setFocus("otherGoal"),
            }}
          />
          {tooManyGoals && (
            <p className="mt-2 text-sm text-red-600">
              You cannot select more than 2 goals. Please uncheck one to
              continue.
            </p>
          )}
        </FormField>

        <ConditionalField show={watchedGoals.includes("Other")} indent>
          <FormField error={errors.otherGoal?.message}>
            <Controller
              name="otherGoal"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <Textarea
                  value={field.value}
                  onValueChange={field.onChange}
                  maxLength={otherGoalField.maxLength}
                  placeholder={otherGoalField.placeholder}
                />
              )}
            />
          </FormField>
        </ConditionalField>
        <p className="text-xs text-gray-500">
          This is the first question. You can update answers anytime during the
          quiz.
        </p>
      </DiscoveryStep.Card>
      <DiscoveryStep.Control
        type="submit"
        loading={isSubmitting}
        disabled={!isValid}
      />
    </form>
  );
};

function DiscoveryStep1() {
  return <IdentifySpecificGoalsForm />;
}

export const Route = createFileRoute("/_public/discovery/steps/1/")({
  head: () =>
    createBrandMeta({
      title: "Dream Coach Discovery Tool",
      description:
        "Find a perfect coach at Dream Coach Match. Answer a few questions and get matched with verified experts for your goals.",
      ogUrl: "https://dreamcoachmatch.com/discovery",
      ogImage: "https://dreamcoachmatch.com/img/og/discovery-landing.jpg",
      keywords: [
        "coach discovery tool",
        "find a coach",
        "personalized coach matching",
        "life coach quiz",
        "business coach matching",
      ],
    }),
  component: DiscoveryStep1,
});
